import { workflowStepMap } from '../workflow/schema';

/**
 * Escapes regex special characters in a string to prevent ReDoS attacks
 * @param str - The string to escape
 * @returns The escaped string safe for use in RegExp
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Converts a route template to a safe regex pattern
 * @param routeTemplate - Route template (e.g. '/$retailer/item-selection')
 * @param placeholder - The placeholder to replace (default: '$retailer')
 * @param pattern - The regex pattern to use as replacement (default: '[^/]+')
 * @returns Safe regex pattern string
 */
const routeToRegexPattern = (
  routeTemplate: string,
  placeholder: string = '$retailer',
  pattern: string = '[^/]+',
): string => {
  const escaped = escapeRegex(routeTemplate);
  const escapedPlaceholder = escapeRegex(placeholder);
  return escaped.replace(escapedPlaceholder, pattern);
};

/**
 * Checks if a given route matches any valid workflow step route
 * @param route - The route to check (e.g. '/$retailer/item-selection')
 * @returns boolean indicating if the route is a valid workflow step
 */
export const isValidWorkflowRoute = (route: string): boolean => {
  // Get all valid workflow routes
  const validRoutes = Object.values(workflowStepMap).map((step) => step.route);

  // Check if the route matches any valid workflow route pattern
  return validRoutes.some((validRoute) => {
    const routePattern = routeToRegexPattern(validRoute);
    const regex = new RegExp(`^${routePattern}$`);
    return regex.test(route);
  });
};

/**
 * Whether a concrete pathname matches a step's route template. Step routes are
 * `$retailer` templates while router locations resolve to concrete pathnames
 * (e.g. `/acme/item-selection`), so a plain string compare never matches.
 * @param pathname - Resolved pathname (e.g. '/acme/item-selection')
 * @param routeTemplate - Step route template (e.g. '/$retailer/item-selection')
 */
export const pathnameMatchesRoute = (
  pathname: string,
  routeTemplate: string,
): boolean => {
  const regex = new RegExp(`^${routeToRegexPattern(routeTemplate)}$`);
  return regex.test(pathname);
};
